/**
 * 입력. 키보드(WASD/방향키)와 가상 조이스틱(터치)을 하나의 방향 벡터로 합친다.
 * dx, dy 는 항상 길이 0~1 로 정규화되어 있다.
 */
export class Input {
  dx = 0;
  dy = 0;

  /** 마지막으로 움직인 방향. 멈춰도 유지된다(근접 무기 방향용). */
  faceX = 1;
  faceY = 0;

  private readonly keys = new Set<string>();
  private pointerId: number | null = null;
  private originX = 0;
  private originY = 0;
  private stickX = 0;
  private stickY = 0;
  private stickActive = false;

  private readonly stickRadius = 55;
  private readonly onKeyDown: (e: KeyboardEvent) => void;
  private readonly onKeyUp: (e: KeyboardEvent) => void;
  private readonly onBlur: () => void;

  /** 조이스틱 시각화를 UI가 읽어간다. */
  get stick(): { active: boolean; ox: number; oy: number; kx: number; ky: number; radius: number } {
    return {
      active: this.stickActive,
      ox: this.originX,
      oy: this.originY,
      kx: this.stickX,
      ky: this.stickY,
      radius: this.stickRadius,
    };
  }

  /** ESC 등 일회성 키를 게임이 구독한다. */
  onKey: ((code: string) => void) | null = null;

  constructor(private readonly surface: HTMLElement) {
    this.onKeyDown = (e) => {
      this.keys.add(e.code);
      // Backquote(`) 는 개발자 패널 (개발 모드가 아니면 main.ts 에서 무시한다)
      // 1.24: Shift = 대시(누구나), Space = 액티브 1(사냥 준비·회광반조), E = 액티브 2
      const once =
        e.code === 'Escape' || e.code === 'KeyP' || e.code === 'KeyM' || e.code === 'Backquote' || e.code === 'Space' ||
        e.code === 'KeyE' || e.code === 'ShiftLeft' || e.code === 'ShiftRight';
      if ((once || /^Digit[1-9]$/.test(e.code)) && !e.repeat) {
        this.onKey?.(e.code);
      }
      // Space 는 페이지를 내리거나 포커스된 버튼을 누르지 않게 막는다
      if (MOVE_CODES.has(e.code) || e.code === 'Space') e.preventDefault();
    };
    this.onKeyUp = (e) => {
      this.keys.delete(e.code);
      if (e.code === 'Space') e.preventDefault();
    };
    this.onBlur = () => this.keys.clear();

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);

    surface.addEventListener('pointerdown', this.handleDown, { passive: false });
    surface.addEventListener('pointermove', this.handleMove, { passive: false });
    surface.addEventListener('pointerup', this.handleUp);
    surface.addEventListener('pointercancel', this.handleUp);
  }

  private handleDown = (e: PointerEvent): void => {
    if (e.pointerType === 'mouse') return; // 마우스로는 조이스틱을 쓰지 않는다
    if (this.pointerId !== null) return;
    this.pointerId = e.pointerId;
    this.originX = e.clientX;
    this.originY = e.clientY;
    this.stickX = e.clientX;
    this.stickY = e.clientY;
    this.stickActive = true;
    e.preventDefault();
  };

  private handleMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.stickX = e.clientX;
    this.stickY = e.clientY;
    e.preventDefault();
  };

  private handleUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.pointerId = null;
    this.stickActive = false;
  };

  /** 매 프레임 한 번. */
  update(): void {
    let x = 0;
    let y = 0;

    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) x -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) x += 1;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) y -= 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) y += 1;

    if (this.stickActive) {
      const sx = this.stickX - this.originX;
      const sy = this.stickY - this.originY;
      const len = Math.hypot(sx, sy);
      if (len > 6) {
        const clamped = Math.min(len, this.stickRadius) / this.stickRadius;
        x += (sx / len) * clamped;
        y += (sy / len) * clamped;
      }
    }

    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    this.dx = x;
    this.dy = y;

    if (len > 0.01) {
      this.faceX = x / Math.max(len, 1);
      this.faceY = y / Math.max(len, 1);
    }
  }

  reset(): void {
    this.keys.clear();
    this.pointerId = null;
    this.stickActive = false;
    this.dx = 0;
    this.dy = 0;
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    this.surface.removeEventListener('pointerdown', this.handleDown);
    this.surface.removeEventListener('pointermove', this.handleMove);
    this.surface.removeEventListener('pointerup', this.handleUp);
    this.surface.removeEventListener('pointercancel', this.handleUp);
  }
}

const MOVE_CODES = new Set([
  'KeyW', 'KeyA', 'KeyS', 'KeyD',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'Space',
]);
